import Handle from '~/prix/react/components/handle'
import Header from '~/components/card/header'
import styled from 'styled-components'
import theme from '~/design'
import { Input } from '~/packages/general/detailedInformation/styles'
import Label from '~/components/label'
import { useNavigate, useParams } from 'react-router'
import userFetchDefinition from './fetch/userFetch.definition'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useActionFunction from '~/prix/react/hooks/actionFunction'
import { schemaUserCreate, schemaUserUpdate, userRoleEnumerated } from './userObject'
import EnumeratedField from '~/prix/react/components/form/field/enumeratedField'
import { ButtonVariants } from '../../../components/buttonVariants'
import userCreateUserDefinition from './create/userCreate.definition'
import userUpdateDefinition from './update/userUpdate.definition'
import { toastError, toastSuccess } from '~/components/toasts'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import useAPI from '~/prix/react/hooks/api'
import { getManageableRolesForRole } from './userManagementPermissions'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 870px) {
    padding: 30px 0;
  }
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${({ theme }) => theme.padding(1)};
  span {
    margin: 0;
  }
`

const PasswordFieldWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  ${Input} {
    padding-right: 45px;
  }
`

const PasswordToggleButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.darkGrey};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.padding(1)};
`

const Container = styled.div`
  padding: ${({ theme }) => theme.padding(2)};
  background-color: #fff;
  margin-bottom: 1rem;
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius};
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius};
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  form {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.padding(2)};
  }
`

const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 12px;
  margin-top: 4px;
`

const HelperMessage = styled.span`
  color: ${({ theme }) => theme.colors.darkGrey};
  font-size: 12px;
  margin-top: 4px;
`

type UserFormData = {
  name: string
  email: string
  role: string
  password?: string
  confirmPassword?: string
}

function getUserSaveErrorMessage(error: unknown) {
  const message = error instanceof Error ? error.message : String(error)
  const normalizedMessage = message.toLowerCase()

  if (
    normalizedMessage.includes('já existe') ||
    normalizedMessage.includes('already exists') ||
    normalizedMessage.includes('email-already-exists')
  ) {
    return 'Já existe um usuário com este e-mail.'
  }

  return `Erro ao salvar usuário: ${message}`
}

const UserUpsertScreen = (): React.ReactElement => {
  const { id } = useParams() as { id: string }
  const navigate = useNavigate()
  const [userData, setUserData] = useState<UserFormData | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { context } = useAPI()
  const actorRole = context.user?.activeClient?.roles?.[0]
  const manageableRoles = useMemo(() => getManageableRolesForRole(actorRole), [actorRole])
  const validationSchema = useMemo(() => (id ? schemaUserUpdate : schemaUserCreate), [id])

  const { callAction: callUserFetch, isLoading } = useActionFunction(userFetchDefinition, {
    initialIsLoading: false,
  })
  const { callAction: callUserCreate, isLoading: isLoadingCreate } = useActionFunction(
    userCreateUserDefinition,
    { initialIsLoading: false },
  )
  const { callAction: callUserUpdate, isLoading: isLoadingUpdate } = useActionFunction(
    userUpdateDefinition,
    { initialIsLoading: false },
  )

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<UserFormData>({
    resolver: zodResolver(validationSchema) as never,
    defaultValues: {
      name: '',
      email: '',
      role: '',
      password: '',
      confirmPassword: '',
    },
    mode: 'onSubmit',
  })

  useEffect(() => {
    reset()
    setValue('name', userData?.name ? userData.name : '')
    setValue('email', userData?.email ? userData.email : '')
    setValue('role', userData?.role ? userData.role : '')
    setValue('password', userData?.password ? userData.password : '')
    setValue('confirmPassword', userData?.confirmPassword ? userData.confirmPassword : '')
  }, [userData])

  useEffect(() => {
    if (id) {
      handleFetchUser()
    }
  }, [id])

  const handleFetchUser = useCallback(async () => {
    const { resultPromise } = callUserFetch({ id })
    const result = await resultPromise
    setUserData({
      name: result?.data?.[0].name,
      email: result?.data?.[0].email,
      role: result?.data?.[0].role,
      password: '',
      confirmPassword: '',
    })
  }, [id, callUserFetch])

  const onSubmit = useCallback(
    async (data: UserFormData) => {
      try {
        const actionData = {
          name: data.name,
          email: data.email,
          role: data.role as any,
        }

        if (id) {
          const { resultPromise } = callUserUpdate({
            id,
            ...actionData,
            ...(data.password ? { password: data.password } : {}),
          })
          await resultPromise
          toastSuccess('Usuário atualizado com sucesso')
        } else {
          const { resultPromise } = callUserCreate({
            ...actionData,
            password: data.password ?? '',
          })
          await resultPromise
          toastSuccess('Usuário criado com sucesso')
        }

        navigate('/app/users', { replace: true })
      } catch (error: any) {
        console.error('Erro ao salvar usuário:', error)
        toastError(getUserSaveErrorMessage(error))
      }
    },
    [id, callUserCreate, callUserUpdate, navigate],
  )

  return (
    <Handle
      isLoading={isLoading}
      style={{
        height: '100%',
      }}
    >
      <Wrapper>
        <Header title={id ? 'Editar usuário' : 'Criar usuário'} />
        <Container>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Field>
              <Label label='Nome' size='sm' />
              <Controller
                name='name'
                control={control}
                render={({ field }) => (
                  <Input {...field} style={{ pointerEvents: 'auto' }} placeholder='Digite o nome' />
                )}
              />
              {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </Field>

            <Field>
              <Label label='E-mail' size='sm' />
              <Controller
                name='email'
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    type='email'
                    style={{ pointerEvents: 'auto' }}
                    placeholder='Digite o e-mail'
                  />
                )}
              />
              {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
            </Field>

            <Field>
              <Label label='Cargo' size='sm' />
              <Controller
                name='role'
                control={control}
                render={({ field }) => (
                  <EnumeratedField
                    fieldSchema={userRoleEnumerated(manageableRoles)}
                    value={field.value}
                    onDirectChange={field.onChange}
                  />
                )}
              />
              {errors.role && <ErrorMessage>{errors.role.message}</ErrorMessage>}
            </Field>

            <Field>
              <Label label={id ? 'Nova senha' : 'Senha'} size='sm' />
              <Controller
                name='password'
                control={control}
                render={({ field }) => (
                  <PasswordFieldWrapper>
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      style={{ pointerEvents: 'auto' }}
                      placeholder={id ? 'Deixe em branco para manter a senha atual' : 'Digite a senha'}
                    />
                    <PasswordToggleButton
                      type='button'
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </PasswordToggleButton>
                  </PasswordFieldWrapper>
                )}
              />
              {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
              <HelperMessage>Mínimo de 6 caracteres.</HelperMessage>
            </Field>

            <Field>
              <Label label={id ? 'Confirmar nova senha' : 'Confirmar senha'} size='sm' />
              <Controller
                name='confirmPassword'
                control={control}
                render={({ field }) => (
                  <PasswordFieldWrapper>
                    <Input
                      {...field}
                      type={showConfirmPassword ? 'text' : 'password'}
                      style={{ pointerEvents: 'auto' }}
                      placeholder={id ? 'Confirme somente se alterar a senha' : 'Confirme a senha'}
                    />
                    <PasswordToggleButton
                      type='button'
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      title={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    >
                      <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                    </PasswordToggleButton>
                  </PasswordFieldWrapper>
                )}
              />
              {errors.confirmPassword && (
                <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>
              )}
            </Field>
            <Footer>
              <ButtonVariants
                label='Cancelar'
                backgroundColor='transparent'
                color={theme.colors.primary}
                type='button'
                onClick={() => navigate('/app/users')}
              />
              <ButtonVariants
                label='Salvar'
                backgroundColor={theme.colors.success}
                type='submit'
                disabled={isSubmitting || (id ? isLoadingUpdate : isLoadingCreate)}
                isLoading={isSubmitting || (id ? isLoadingUpdate : isLoadingCreate)}
              />
            </Footer>
          </form>
        </Container>
      </Wrapper>
    </Handle>
  )
}

export default UserUpsertScreen
